import classNames from "classnames";

type FixedSize = `${number}px` | `${number}%`;
type FixedSizeAndAuto = FixedSize | "auto";
type FloatingSize = `${number}fr` | FixedSize;

type ColumnSize = "auto" | FloatingSize | `fit-content(${FloatingSize})` | `minmax(${FixedSizeAndAuto}, ${FloatingSize})`;

type ColumnAlignment = "left" | "center" | "right";

export interface TableColumn<T> {
  key: string
  header: string
  hidden?: ((allItems: T[]) => boolean) | boolean
  render: (item: T) => React.ReactNode
  /**
   * Size of the column. It is a narrowed down set of css gird properties. Possible values for reference:
   *
   * * `auto` - The column width is automatically scaled to fit all content.
   * * `100px` - The column width is set to a fixed size of 100 pixels.
   * * `1fr` - The column width is set to a fraction of the available space. For example, if there are two columns with `1fr` each, they will take up equal space.
   * If one of the columns has `2fr`, it will take up twice as much space as the other two. __NOTE__ this differs significantly from `auto` in that it will
   * size the column based on the remaining space not taking into account the content.
   * * `fit-content(100px)` - The column width is set to the minimum size required to fit the content. If the content is larger than 100 pixels, it will be 100 pixels.
   * This is equivalent to `auto` except it sets a maximum size for the column.
   * * `minmax(100px, 1fr)` - The column width is set to a minimum of 100 pixels and a maximum of 1 fraction of the available space. This is the same as `1fr` except
   * it sets a minimum size for the column.
   *
   * In all places where a pixel value is used, it can be replaced with a percentage value. For example, `20%` is a valid value. The percentage value is the percentage
   * of the parent grid. For example, if the parent grid has 1000 pixels, a column with `20%` will be 200 pixels wide.
   *
   * For more details refer to https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns
   */
  size?: ColumnSize
  align?: ColumnAlignment
  width?: number
  flex?: number
  className?: string | ((item: T) => string)
  style?: React.CSSProperties
}

export const getColumnClassName = <T>(column: TableColumn<T>, item?: T) => {
  let className: string;

  if (typeof column.className === "function") {
    className = column.className(item);
  } else {
    className = column.className;
  }

  return classNames(className, {
    "table-align-right": column.align === "right",
    "table-align-center": column.align === "center"
  })
};

// used for flex
export const getColumnStyle = (column: TableColumn<any>) => {
  if (column.flex == null && !column.width) {
    return { ...column.style, flex: 1 };
  }

  if (column.width) {
    return { ...column.style, width: column.width };
  }

  return { ...column.style, flex: column.flex };
};
